import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Hash, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { InvoiceRunType } from '@/graphql/generatedTypes';
import { Routes } from '@/utils/routes';
import { cn } from '@/lib/utils';

import { useNextFreeEsInvoiceNumberLazyQuery } from './NextFreeEsInvoiceNumber.generated';
import { useStartInvoiceRunMutation } from './StartInvoiceRun.generated';
import { useRetryInvoiceRunMutation } from './RetryInvoiceRun.generated';
import type { RunRow } from './RunHistoryTable';
import {
  formatDateLong,
  formatDateTimeShort,
  formatEsNumber,
  parsePeriodValue,
  periodLabel,
  recentPeriodOptions,
  runPresentationState,
  runTypeLongLabel,
  shortRunId,
} from './invoiceRunPresentation';

export type StartRunType = InvoiceRunType.Es | InvoiceRunType.Default;

type StartRunSheetProps = {
  /** Which run type the sheet is opened for; null = closed. */
  type: StartRunType | null;
  /** Already-loaded runs, for the duplicate-period guard. */
  runs: RunRow[];
  onOpenChange: (open: boolean) => void;
  onStarted: () => void;
};

const TYPE_SUB: Record<StartRunType, string> = {
  [InvoiceRunType.Es]: 'Spain only · takes about an hour',
  [InvoiceRunType.Default]: 'All countries except Spain · takes about 8 hours',
};

const TYPE_DURATION: Record<StartRunType, string> = {
  [InvoiceRunType.Es]: 'About an hour',
  [InvoiceRunType.Default]: 'About 8 hours',
};

const REVIEW_INTRO: Record<StartRunType, string> = {
  [InvoiceRunType.Es]:
    'This starts a Spanish generation run: draft invoices for all Spanish stores, numbered sequentially.',
  [InvoiceRunType.Default]:
    'This starts a default generation run: draft invoices for every country except Spain.',
};

function guardStateDescription(run: RunRow): string {
  const state = runPresentationState(run);
  switch (state) {
    case 'scheduled':
      return run.due_at
        ? `scheduled to start ${formatDateLong(run.due_at)}`
        : 'scheduled';
    case 'starting':
      return 'about to start';
    case 'running':
    case 'stalled':
      return run.started_at
        ? `running (started ${formatDateTimeShort(run.started_at)})`
        : 'running';
    case 'done':
      return run.finished_at
        ? `done (finished ${formatDateTimeShort(run.finished_at)})`
        : 'done';
    case 'failed':
      return run.started_at
        ? `failed (started ${formatDateTimeShort(run.started_at)})`
        : 'failed';
  }
}

export function StartRunSheet({
  type,
  runs,
  onOpenChange,
  onStarted,
}: StartRunSheetProps) {
  const history = useHistory();
  const open = type != null;
  const isEs = type === InvoiceRunType.Es;

  const [step, setStep] = useState<'form' | 'review'>('form');
  const [periodVal, setPeriodVal] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [esInput, setEsInput] = useState('');
  const [suggestion, setSuggestion] = useState<number | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [fetchSuggestion, { loading: suggestionLoading }] =
    useNextFreeEsInvoiceNumberLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const next = data.nextFreeEsInvoiceNumber;
        setSuggestion(next);
        setFetchedAt(Date.now());
        // Prefill unless the user already typed something else.
        setEsInput((prev) => (prev === '' ? String(next) : prev));
      },
    });

  const [startRun] = useStartInvoiceRunMutation();
  const [retryRun] = useRetryInvoiceRunMutation();

  const periodOptions = useMemo(
    () => (type ? recentPeriodOptions(type) : []),
    [type],
  );

  // Reset per open; ES sheets fetch the Billomat suggestion on open.
  useEffect(() => {
    if (!type) return;
    setStep('form');
    setDryRun(false);
    setEsInput('');
    setSuggestion(null);
    setFetchedAt(null);
    setPeriodVal(recentPeriodOptions(type)[0].value);
    if (type === InvoiceRunType.Es) {
      void fetchSuggestion();
    }
  }, [type, fetchSuggestion]);

  const period = periodVal ? parsePeriodValue(periodVal) : null;
  const periodText =
    type && period ? periodLabel(isEs, period.year, period.isoMonth) : '';

  // Guard: a live (non-dry) run of this type already exists for the period.
  const existingRun = useMemo(() => {
    if (!type || !period) return undefined;
    return runs.find(
      (r) =>
        r.type === type &&
        r.year === period.year &&
        r.iso_month === period.isoMonth &&
        !r.dry_run,
    );
  }, [runs, type, period]);
  const existingFailed =
    existingRun && runPresentationState(existingRun) === 'failed';
  // A dry run may be repeated freely; only a live start is guarded.
  const guardBlocks = !dryRun && existingRun != null;

  const esNumber = esInput === '' ? null : Number.parseInt(esInput, 10);
  const esTooLow =
    isEs && esNumber != null && suggestion != null && esNumber < suggestion;
  const esMissing = isEs && (esNumber == null || Number.isNaN(esNumber));
  const formInvalid = guardBlocks || (isEs && (esMissing || esTooLow));

  const esEdited =
    suggestion != null && esInput !== '' && esInput !== String(suggestion);

  const handleStart = useCallback(async () => {
    if (!type || !period) return;
    setSubmitting(true);
    try {
      await startRun({
        variables: {
          type,
          year: period.year,
          iso_month: period.isoMonth,
          dry_run: dryRun,
          next_es_invoice_number: isEs ? esNumber : null,
        },
      });
      toast.success(
        `${runTypeLongLabel(type)}${dryRun ? ' (dry run)' : ''} queued for ${periodText}`,
      );
      onStarted();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Starting the run failed');
    } finally {
      setSubmitting(false);
    }
  }, [
    type,
    period,
    dryRun,
    isEs,
    esNumber,
    periodText,
    startRun,
    onStarted,
    onOpenChange,
  ]);

  const handleRetryExisting = useCallback(async () => {
    if (!existingRun) return;
    try {
      await retryRun({ variables: { id: existingRun.id } });
      toast.success(`Run ${shortRunId(existingRun.id)} queued for retry`);
      onStarted();
      onOpenChange(false);
      history.push(Routes.monthlyInvoicingRun(existingRun.id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed');
    }
  }, [existingRun, retryRun, onStarted, onOpenChange, history]);

  if (!type) return null;

  const fetchedAgo =
    fetchedAt == null
      ? null
      : Date.now() - fetchedAt < 60_000
        ? 'fetched just now'
        : `fetched ${Math.floor((Date.now() - fetchedAt) / 60_000)} min ago`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="tw:w-[440px] tw:gap-0 tw:sm:max-w-[440px]"
      >
        <SheetHeader>
          <SheetTitle>
            {isEs ? 'Start Spanish run' : 'Start default run'}
          </SheetTitle>
          <SheetDescription>{TYPE_SUB[type]}</SheetDescription>
        </SheetHeader>

        {step === 'form' ? (
          <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-5 tw:overflow-y-auto tw:px-4">
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Label htmlFor="billing-period">Billing period</Label>
              <Select value={periodVal} onValueChange={setPeriodVal}>
                <SelectTrigger id="billing-period" className="tw:w-full">
                  <SelectValue placeholder="Choose a period" />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                      {option.current ? ' (current)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="tw:text-xs tw:text-muted-foreground">
                Pre-filled with the period that just ended. Change it only to
                catch up on an older period.
              </p>
            </div>

            {guardBlocks && existingRun && (
              <div className="tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:border-destructive/40 tw:bg-destructive/5 tw:p-3">
                <p className="tw:text-sm tw:font-semibold tw:text-destructive">
                  A run already exists for this period
                </p>
                <p className="tw:text-sm tw:text-foreground">
                  Run{' '}
                  <span className="tw:font-mono">
                    {shortRunId(existingRun.id)}
                  </span>{' '}
                  for this period is {guardStateDescription(existingRun)}.
                </p>
                <div className="tw:flex tw:gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      history.push(Routes.monthlyInvoicingRun(existingRun.id));
                    }}
                  >
                    Open that run
                  </Button>
                  {existingFailed && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRetryExisting}
                    >
                      Retry it instead
                    </Button>
                  )}
                </div>
              </div>
            )}

            {isEs && (
              <div className="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-foreground tw:p-4">
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Hash className="tw:size-4" />
                  <span className="tw:text-sm tw:font-semibold">
                    Next Spanish invoice number
                  </span>
                </div>
                <p className="tw:text-xs tw:leading-relaxed tw:text-muted-foreground">
                  Spain requires one unbroken sequence. One-off invoices are
                  numbered by hand in Billomat between runs, so check this
                  against Billomat before starting — a wrong value breaks a
                  legally required sequence.
                </p>
                <div className="tw:flex tw:items-stretch tw:gap-2">
                  <div className="tw:flex tw:items-center tw:rounded-md tw:border tw:border-input tw:bg-muted tw:px-3 tw:font-mono tw:text-sm tw:text-muted-foreground">
                    ES-
                  </div>
                  <input
                    aria-label="Next Spanish invoice number"
                    className={cn(
                      'tw:h-11 tw:w-28 tw:rounded-md tw:border tw:bg-background tw:px-3 tw:font-mono tw:text-[19px] tw:font-semibold tw:tabular-nums tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
                      esTooLow ? 'tw:border-destructive' : 'tw:border-input',
                    )}
                    inputMode="numeric"
                    maxLength={5}
                    value={esInput}
                    onChange={(e) =>
                      setEsInput(e.target.value.replace(/\D/g, '').slice(0, 5))
                    }
                  />
                  <Button
                    variant="outline"
                    className="tw:size-11"
                    title="Fetch the highest used number from Billomat"
                    disabled={suggestionLoading}
                    onClick={() => void fetchSuggestion()}
                  >
                    <RefreshCw
                      className={cn(suggestionLoading && 'tw:animate-spin')}
                    />
                  </Button>
                </div>
                {suggestion != null && (
                  <p className="tw:text-xs tw:text-muted-foreground">
                    Highest used in Billomat:{' '}
                    <span className="tw:font-mono">
                      {formatEsNumber(suggestion - 1)}
                    </span>
                    {fetchedAgo ? ` · ${fetchedAgo}` : ''}
                    {esEdited && (
                      <>
                        {' · '}
                        <button
                          type="button"
                          className="tw:cursor-pointer tw:font-medium tw:text-emerald-600 tw:hover:underline tw:dark:text-emerald-400"
                          onClick={() => setEsInput(String(suggestion))}
                        >
                          Use suggestion
                        </button>
                      </>
                    )}
                  </p>
                )}
                {suggestion == null && suggestionLoading && (
                  <p className="tw:text-xs tw:text-muted-foreground">
                    Fetching the highest used number from Billomat…
                  </p>
                )}
                {esTooLow && esNumber != null && suggestion != null && (
                  <p className="tw:text-sm tw:font-medium tw:text-destructive">
                    {formatEsNumber(esNumber)} is at or below a number already
                    used in Billomat. Use {formatEsNumber(suggestion)} or
                    higher.
                  </p>
                )}
              </div>
            )}

            <div className="tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:bg-muted/60 tw:p-3">
              <Switch
                id="dry-run"
                checked={dryRun}
                onCheckedChange={setDryRun}
                className="tw:mt-0.5"
              />
              <Label
                htmlFor="dry-run"
                className="tw:flex tw:flex-col tw:items-start tw:gap-1 tw:font-normal"
              >
                <span className="tw:text-sm tw:font-medium">Dry run</span>
                <span className="tw:text-xs tw:text-muted-foreground">
                  Builds the same files without creating invoices in Billomat. A
                  dry run can never be charged.
                </span>
              </Label>
            </div>
          </div>
        ) : (
          <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-4 tw:overflow-y-auto tw:px-4">
            <p className="tw:text-sm tw:text-muted-foreground">
              {REVIEW_INTRO[type]}
            </p>
            <div className="tw:overflow-hidden tw:rounded-lg tw:border tw:border-border">
              {[
                ['Run type', runTypeLongLabel(type)],
                ['Billing period', periodText],
                [
                  'Invoices dated',
                  isEs && period
                    ? formatDateLong(
                        new Date(
                          Date.UTC(period.year, period.isoMonth, 24),
                        ).toISOString(),
                      )
                    : 'Last day of the period',
                ],
                ...(isEs && esNumber != null
                  ? [['Starts at number', formatEsNumber(esNumber)] as const]
                  : []),
                ['Dry run', dryRun ? 'Yes' : 'No'],
                ['Expected duration', TYPE_DURATION[type]],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:text-sm tw:last:border-b-0"
                >
                  <span className="tw:text-muted-foreground">{label}</span>
                  <span
                    className={cn(
                      'tw:text-right tw:font-medium',
                      label === 'Starts at number' && 'tw:font-mono',
                    )}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
            <p className="tw:text-sm tw:text-muted-foreground">
              {dryRun
                ? 'Nothing will be created in Billomat. The result files are still produced so you can review them.'
                : 'Invoices are created as drafts in Billomat. Nothing is sent to partners until someone completes and charges the run.'}
            </p>
          </div>
        )}

        <SheetFooter className="tw:flex-row tw:justify-end tw:gap-2">
          {step === 'review' && (
            <Button
              variant="outline"
              className="tw:mr-auto"
              onClick={() => setStep('form')}
            >
              Back
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {step === 'form' ? (
            <Button disabled={formInvalid} onClick={() => setStep('review')}>
              Review
            </Button>
          ) : (
            <Button disabled={submitting} onClick={handleStart}>
              {dryRun ? 'Start dry run' : 'Start run'}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
